/**
 * @author Hiroki Moto
 */

import React, { Component } from 'react';
import loadable from '@loadable/component';
import { connect } from 'react-redux';
import filter from 'ramda/src/filter';
import find from 'ramda/src/find';
import propEq from 'ramda/src/propEq';
import values from 'ramda/src/values';
import moment from 'moment';
import Avatar from '@components/@core/Avatar';
import DatePicker from '@components/@core/DatePicker';
import ErrorBoundary from '@components/@core/ErrorBoundary';
import EditDropdown from '@components/EditDropdown';
import Filter from '@components/@core/Filter';
import Icon from '@components/@core/Icon';
import Pagination from '@components/@core/Pagination';
import Rating from '@components/@core/Rating';
import Text from '@components/@core/Text';
import ManageActions from '~/actions/manage';
import { convertUrl } from '~/services/util';
import withNavigate from '~/hocs/withNavigate';
import { IndividualPerformancePdf } from './IndividualPerformancePdf';

class IndividualPerformance extends Component {
    constructor(props) {
        super(props);

        const company = find(propEq('id', props.companyId), props.companies) || { id: props.companyId };
        const managers = company.managers || [];
        const roles = company.job_roles || [];

        const id = Number(props.match?.params?.id || 0) || props.userId;
        let user = find(propEq('id', id), props.employees) || { profile: {} };
        const job = find(propEq('id', user.job_role_id), roles) || {};
        user = { ...user, job };
        const manager = props.role < 4 ? props.user : find((item) => item.id == user.manager_id, managers);

        this.state = {
            page: 1,
            per: 25,
            user,
            manager,
            company,
            managers,
            roles,
            dateStart: moment().subtract(12, 'month').startOf('month').format('YYYY-MM-DD'),
            dateEnd: moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD'),
        };
    }

    componentDidMount() {
        const { user, dateStart, dateEnd } = this.state;
        const { match, companyId } = this.props;
        
        // Extract userId from URL params, fallback to user.id
        const userId = Number(match?.params?.id) || user.id;
        // Use user's company_id if available, otherwise use props companyId
        const userCompanyId = user.company_id || companyId;
        
        if (userId && userCompanyId) {
            const payload = { userId, companyId: userCompanyId, dateStart, dateEnd };
            this.props.fetchIndividualPerformanceReview(payload, null);
        }
    }

    handleDateChange = (e) => {
        const { user, per, page } = this.state;
        const { params, companyId } = this.props;
        const dateStart = moment(e.start).format('YYYY-MM-DD');
        const dateEnd = moment(e.end).format('YYYY-MM-DD');
        
        // Extract userId from URL params, fallback to user.id
        const userId = Number(params?.id) || user.id;
        // Use user's company_id if available, otherwise use props companyId
        const userCompanyId = user.company_id || companyId;
        
        this.props.fetchPersonalReviews(userId, {
            company_id: userCompanyId,
            page,
            per_page: per,
            date_start: dateStart,
            date_end: dateEnd,
        });
        const payload = { userId, companyId: userCompanyId, dateStart, dateEnd };
        this.props.fetchIndividualPerformanceReview(payload, null);
        this.setState({ dateStart, dateEnd });
    };

    handleDetail = (e) => () => {
        const userId = e.user_id;
        const companyId = e.company_id;
        this.props.fetchIndividualPerformanceReview(
            { userId, companyId, date_start: e.date_start, date_end: e.date_end },
            `/hcm/performance-reviews/${userId}?date_start=${e.date_start}&editable=${false}`
        );
    };

    handleFilter = (type, e) => {
        const { managers, roles, per, dateStart, dateEnd } = this.state;
        const { employees } = this.props;
        if (type === 'employee') {
            if (e[0].id < 0) return;
            const user = find(propEq('id', e[0].id), employees) || { profile: {} };
            const job = find(propEq('id', user.job_role_id), roles) || {};
            const manager = find((item) => item.id == user.manager_id, managers);
            this.setState({ user: { ...user, job }, manager, page: 1 });
            this.props.fetchPersonalReviews(user.id, {
                company_id: user.company_id,
                page: 1,
                per_page: per,
                date_start: dateStart,
                date_end: dateEnd,
            });
            this.props.fetchIndividualPerformanceReview(
                {
                    userId: user.id,
                    companyId: user.company_id,
                    dateStart,
                    dateEnd,
                },
                null
            );
        } else if (type == 'company') {
            this.setState({ company: e[0] });
        }
    };

    handlePagination = (page) => {
        const { user, per, dateStart, dateEnd } = this.state;
        this.props.fetchPersonalReviews(user.id, {
            company_id: user.company_id,
            page,
            per_page: per,
            date_start: dateStart,
            date_end: dateEnd,
        });
        this.setState({ page });
    };

    handlePer = (per) => {
        const { user, page, dateStart, dateEnd } = this.state;
        this.props.fetchPersonalReviews(user.id, {
            company_id: user.company_id,
            page,
            per_page: per,
            date_start: dateStart,
            date_end: dateEnd,
        });
        this.setState({ per });
    };

    handleReportMenu = (e) => {
        const { user, company, dateStart, dateEnd } = this.state;
        const { data, individualPerformanceReview } = this.props;
        const lastReviewState = this.props.location?.state?.lastReview;
        IndividualPerformancePdf({
            user,
            company,
            dateStart,
            dateEnd,
            data,
            lastReviewState,
            individualPerformanceReview,
        });
    };

    render() {
        const { user, company, dateStart, dateEnd } = this.state;
        const { data } = this.props;
        const { current_page, last_page } = data;
        const date = moment.range(dateStart, dateEnd);
        const reviews = filter((item) => moment(item.date_start).isBetween(dateStart, dateEnd, null, '[]'), data.data);
        return (
            <ErrorBoundary className="ind-performance-reports">
                <Filter 
                    back="all reviews" 
                    onChange={this.handleFilter}
                    defaultIds={[[company.id], [user.id]]}  
                    multi={[false, false]} 
                />
                <div className="card mt-3">
                    <div className="d-flex jc-between pt-4 pt-lg-0">
                        <span className="dsl-b22 bold">Monthly Coaching History</span>
                        {/*<EditDropdown className="ms-3" dataCy="editDropdown" options={['Print']} onChange={this.handleReportMenu} /> */}
                        <DatePicker calendar="range" append="caret" format="MMM YYYY" as="span" align="right" value={date} mountEvent onSelect={this.handleDateChange} />
                    </div>
                    <div className="mt-4 d-flex w-100 flex-sm-column ai-sm-center">
                        <Avatar className="me-0 me-lg-4" name={user.name} url={convertUrl(user.profile.avatar)} size="large" />
                    </div>
                    <div className="list-item mt-2 pb-2 d-none d-md-flex">
                        <div className="d-flex-2">
                            <span className="dsl-m12">Date</span>
                        </div>
                        <div className="d-flex-4">
                            <span className="dsl-m12">Scorecard</span>
                        </div>
                        <div className="d-flex-1" />
                        <div className="d-flex-3">
                            <span className="dsl-m12">Score</span>
                        </div>
                        <div className="d-flex-2">
                            <span className="dsl-m12">Commitments</span>
                        </div>
                        <div className="d-flex-1" />
                        <div className="d-flex-2">
                            <span className="dsl-m12">Status</span>
                        </div>
                    </div>

                    {reviews.length ? (
                        reviews.map((item) => {
                            const scorecard = item.data?.scorecards ? values(item.data.scorecards)[0] : null;
                            return (
                                <div className="list-item list cursor-pointer" key={item.id} onClick={this.handleDetail(item)}>
                                    <div className="d-flex-2 d-flex ai-center">
                                        <label className="mobile-label d-md-none dsl-m12 m-0">Date</label>
                                        <span className="dsl-b14 ms-md-2 cursor-pointer">{moment(item.date_start).format('MMMM YYYY')}</span>
                                    </div>
                                    <div className="d-flex-4 truncate-one d-flex ai-center">
                                        <label className="mobile-label d-md-none dsl-m12 m-0">Scorecard</label>
                                        <span className="dsl-b14">{scorecard?.title || 'No Scorecard'}</span>
                                    </div>
                                    <div className="d-flex-1" />
                                    <div className="d-flex-3 d-flex ai-center">
                                        <label className="mobile-label d-md-none dsl-m12 m-0">Score</label>
                                        {item.status == 2 ? (
                                            <>
                                                {item?.data?.completed_average_star_rating !== 'N/A' ? (
                                                    <Rating score={item?.data?.completed_average_star_rating} />
                                                ) : (
                                                    <span className="dsl-b14">Na (Per Manager)</span>
                                                )}
                                                {item?.data?.average_star_rating === undefined && <span className="dsl-b14">Incomplete</span>}
                                            </>
                                        ) : (
                                            <span className="dsl-b14">Incomplete</span>
                                        )}
                                    </div>
                                    <div className="d-flex-2 d-flex ai-center">
                                        <label className="mobile-label d-md-none dsl-m12 m-0">Commitments</label>
                                        {item.status == 2 ? (
                                            <>
                                                <Icon name="fas fa-graduation-cap" color="#343f4b" size={14} />
                                                <span className="dsl-b14 ms-1">{item.trainings?.length ?? 0}</span>
                                                <Icon name="fas fa-circle-check ms-2" color="#343f4b" size={14} />
                                                <span className="dsl-b14 ms-1">{item.tasks?.length ?? 0}</span>
                                            </>
                                        ) : (
                                            <span className="dsl-b14">Incomplete</span>
                                        )}
                                    </div>
                                    <div className="d-flex-1" />
                                    <div className="d-flex-2 d-flex ai-center">
                                        <label className="mobile-label d-md-none dsl-m12 m-0">Status</label>
                                        {item.status == 2 ? (
                                            <>
                                                {item.data?.scorecards ? (
                                                    <span className="dsl-m12">
                                                        Completed{`\n`}
                                                        {moment(item.completed_at).format('MMM DD,YY')}
                                                    </span>
                                                ) : (
                                                    <span className="dsl-b14">No Scorecard</span>
                                                )}
                                            </>
                                        ) : (
                                            <span className="dsl-b14">Incomplete</span>
                                        )}
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        <p className="dsl-b14 text-400 my-4">No Prior Coaching</p>
                    )}

                    <Pagination current={current_page} total={last_page} onChange={this.handlePagination} onPer={this.handlePer} />
                </div>
            </ErrorBoundary>
        );
    }
}

const mapStateToProps = (state) => ({
    isManager: state.app.app_role_id < 4,
    userId: state.app.id,
    companyId: state.app.company_info.id,
    companies: state.app.companies,
    employees: state.app.users,
    data: state.manage.performanceReviews,
    individualPerformanceReview: state.manage.individualPerformanceReview,
});

const mapDispatchToProps = (dispatch) => ({
    fetchPersonalReviews: (id, e) => dispatch(ManageActions.getperformancereviewsRequest(id, e, null)),
    fetchIndividualPerformanceReview: (e, route) => dispatch(ManageActions.individualperformancereviewRequest(e, route)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withNavigate(IndividualPerformance));
